<template>
  <CModal
    :show.sync="showModal"
    :title="title"
    color="info"
    size="lg"
    class="basic-modal-color"
  >
    <CRow class="mb-2">
      <CCol md="12">
        <div class="chat-header">
          <div><strong>Invitado:</strong> {{ guestName || '-' }}</div>
          <div><strong>Whatsapp:</strong> {{ guestPhone || '-' }}</div>
        </div>
      </CCol>
    </CRow>

    <CRow>
      <CCol md="12">
        <div class="chat-shell">
          <div v-if="messages.length === 0" class="chat-empty">
            No hay mensajes para esta conversación.
          </div>

          <div
            v-for="(item, index) in messages"
            :key="index"
            :class="['chat-row', item.sender === 'confirmame' ? 'chat-row--out' : 'chat-row--in']"
          >
            <div :class="['chat-bubble', item.sender === 'confirmame' ? 'chat-bubble--out' : 'chat-bubble--in']">
              <div class="chat-meta">{{ item.sender_label }}</div>
              <div v-if="item.message" class="chat-text">{{ item.message }}</div>
              <div
                v-if="item.sender === 'confirmame' && item.deliveryStatus"
                :class="['chat-delivery', `chat-delivery--${item.deliveryStatusCode || 'sent'}`]"
              >
                <span class="chat-delivery__checks">✓✓</span>
                <span>{{ item.deliveryStatus }}</span>
                <span v-if="item.deliveryStatusDate" class="chat-delivery__time">{{ formatDate(item.deliveryStatusDate) }}</span>
              </div>
              <div class="chat-date">{{ formatDate(item.date) }}</div>
            </div>
          </div>
        </div>
      </CCol>
    </CRow>

    <template #footer>
      <CButton color="secondary" @click="closeModal">Cerrar</CButton>
    </template>
  </CModal>
</template>

<script>
import ws from '@/services/events_guests'
import useDateFormatter from '@/composables/useDateFormatter'
import store from '@/store'

const { formatDateTime } = useDateFormatter()

export default {
  name: 'GuestConversationModal',
  data() {
    return {
      showModal: false,
      title: 'Conversación de WhatsApp',
      guestId: null,
      guestName: '',
      guestPhone: '',
      messages: []
    }
  },
  methods: {
    async showConversationModal(guest) {
      this.guestId = guest && guest.id ? guest.id : null
      this.guestName = [guest && guest.name ? guest.name : '', guest && guest.last_name ? guest.last_name : ''].join(' ').trim()
      this.guestPhone = guest && guest.phone ? guest.phone : ''
      this.messages = []
      this.showModal = true

      if (!this.guestId) {
        return
      }

      this.loading()
      const response = await ws.getConversation({ id: this.guestId })
      if (response.type === 'success') {
        const rows = Array.isArray(response.data) ? response.data : []
        const normalized = rows.map(item => this.normalizeMessageRow(item))
        this.messages = this.mergeStatusWithPreviousOutbound(normalized)
      } else {
        this.showToast(response.type, response.message)
      }
      this.loaded()
    },
    normalizeMessageRow(item) {
      const row = item || {}

      return {
        ...row,
        direction: row.direction || '',
        messageWamid: row.message_wamid || '',
        contextWamid: row.context_wamid || '',
        sourceId: row.source_id || null,
        statusCode: this.resolveStatusCode(row.status, row.message),
        isStatusEvent: this.isStatusEvent(row.status, row.message),
        isDetachedStatus: false,
        deliveryStatus: '',
        deliveryStatusCode: '',
        deliveryStatusDate: null,
        message: this.mapReadableMessage(row.message, row.status),
      }
    },
    mergeStatusWithPreviousOutbound(rows) {
      const merged = []

      rows.forEach((row) => {
        if (!row.isStatusEvent) {
          merged.push(row)
          return
        }

        const target = this.findOutboundForStatus(merged, row)
        const statusLabel = this.statusLabelForCode(row.statusCode, row.message)

        if (!target) {
          return
        }

        const currentRank = this.deliveryStatusRank(target.deliveryStatusCode)
        const incomingRank = this.deliveryStatusRank(row.statusCode)

        if (incomingRank >= currentRank) {
          target.deliveryStatus = statusLabel
          target.deliveryStatusCode = row.statusCode
          target.deliveryStatusDate = row.date
        }
      })

      return merged
    },
    findOutboundForStatus(items, statusRow) {
      const statusWamid = String(statusRow && statusRow.messageWamid ? statusRow.messageWamid : '').trim()

      if (statusWamid) {
        for (let i = items.length - 1; i >= 0; i -= 1) {
          const item = items[i]
          if (!item || item.sender !== 'confirmame' || item.isDetachedStatus) {
            continue
          }

          const outboundWamid = String(item.messageWamid || '').trim()
          if (outboundWamid && outboundWamid === statusWamid) {
            return item
          }
        }
      }

      return this.findPreviousOutboundMessage(items)
    },
    findPreviousOutboundMessage(items) {
      for (let i = items.length - 1; i >= 0; i -= 1) {
        const item = items[i]
        if (item && item.sender === 'confirmame' && !item.isDetachedStatus) {
          return item
        }
      }

      return null
    },
    isStatusEvent(status, text) {
      const code = this.resolveStatusCode(status, text)
      return ['sent', 'delivered', 'read', 'failed', 'undelivered'].includes(code)
    },
    resolveStatusCode(status, text) {
      const normalized = String(status || '').trim().toLowerCase()
      if (['sent', 'delivered', 'read', 'failed', 'undelivered'].includes(normalized)) {
        return normalized
      }

      const message = String(text || '').trim().toLowerCase()
      if (!message.startsWith('[estado]')) {
        return ''
      }

      if (message.includes('leido') || message.includes('leído')) {
        return 'read'
      }
      if (message.includes('entregado')) {
        return 'delivered'
      }
      if (message.includes('fallo') || message.includes('falló') || message.includes('error')) {
        return 'failed'
      }
      if (message.includes('enviado')) {
        return 'sent'
      }

      return ''
    },
    statusLabelForCode(code, fallbackText = '') {
      const normalized = String(code || '').trim().toLowerCase()
      if (normalized === 'read') return 'Leido'
      if (normalized === 'delivered') return 'Entregado'
      if (normalized === 'sent') return 'Enviado'
      if (normalized === 'failed' || normalized === 'undelivered') return 'Error de entrega'

      const fallback = String(fallbackText || '').trim()
      return fallback || 'Estado'
    },
    deliveryStatusRank(code) {
      const normalized = String(code || '').trim().toLowerCase()
      if (normalized === 'sent') return 1
      if (normalized === 'delivered') return 2
      if (normalized === 'read') return 3
      if (normalized === 'failed' || normalized === 'undelivered') return 4
      return 0
    },
    mapReadableMessage(text, status) {
      const rawText = String(text || '').trim()
      if (rawText === '') {
        return rawText
      }

      const templatePrefix = 'Template enviado:'
      if (!rawText.startsWith(templatePrefix)) {
        return rawText
      }

      const templateName = rawText.substring(templatePrefix.length).trim()
      if (templateName === '') {
        return 'Plantilla enviada.'
      }

      return this.getTemplateReadableLabel(templateName, status)
    },
    getTemplateReadableLabel(templateName, status) {
      const normalized = String(templateName || '').toLowerCase()
      const normalizedStatus = String(status || '').toLowerCase()

      if (normalized.includes('save_the_date')) {
        return 'Mensaje enviado: Save The Date'
      }

      if (normalized.includes('preinvitation')) {
        return 'Mensaje enviado: Preinvitacion'
      }

      if (normalized.includes('event_reminder')) {
        return 'Mensaje enviado: Recordatorio de Evento'
      }

      if (normalized.includes('table') || normalized.includes('mesa')) {
        return 'Mensaje enviado: Asignacion de mesa'
      }

      if (normalized.includes('ticket') || normalized.includes('boleto')) {
        return 'Mensaje enviado: Envio de boletos'
      }

      if (normalized.includes('restriction')) {
        return 'Mensaje enviado: Confirmacion de restricciones'
      }

      if (normalized.includes('event_confirmation') || normalized.includes('confirm')) {
        return 'Mensaje enviado: Confirmacion de asistencia'
      }

      if (normalized.includes('invitation') || normalized.includes('invitacion')) {
        return 'Mensaje enviado: Invitacion'
      }

      if (normalized.includes('reminder') || normalized.includes('recordatorio') || normalizedStatus === 'reminder') {
        return 'Mensaje enviado: Recordatorio'
      }

      if (normalized.includes('welcome')) {
        return 'Mensaje enviado: Bienvenida'
      }

      return 'Plantilla enviada'
    },
    formatDate(value) {
      if (!value) return '-'
      const formatted = formatDateTime(value)
      return formatted || value
    },
    closeModal() {
      this.showModal = false
      this.guestId = null
      this.messages = []
    },
    showToast(type, message) {
      store.commit('toast', { color: type, message })
    },
    loading() {
      store.commit('loading')
    },
    loaded() {
      store.commit('loaded')
    }
  }
}
</script>

<style scoped>
.basic-modal-color .modal-header {
  background-color: #668269 !important;
}

.chat-header {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  font-size: 12px;
  color: #4a5568;
}

.chat-shell {
  background: linear-gradient(180deg, #f4f6f8 0%, #eef2f5 100%);
  border: 1px solid #d7dee5;
  border-radius: 10px;
  padding: 14px;
  min-height: 360px;
  max-height: 520px;
  overflow-y: auto;
}

.chat-empty {
  text-align: center;
  color: #7b8794;
  font-size: 13px;
  padding: 30px 10px;
}

.chat-row {
  display: flex;
  margin-bottom: 10px;
}

.chat-row--in {
  justify-content: flex-start;
}

.chat-row--out {
  justify-content: flex-end;
}

.chat-bubble {
  max-width: 76%;
  border-radius: 14px;
  padding: 9px 11px;
  box-shadow: 0 2px 8px rgba(16, 24, 40, 0.08);
}

.chat-bubble--in {
  background: #ffffff;
  border: 1px solid #d9e1e8;
}

.chat-bubble--out {
  background: #dff6db;
  border: 1px solid #b9dfb3;
}

.chat-meta {
  font-size: 11px;
  font-weight: 700;
  color: #3f4d5a;
  margin-bottom: 4px;
}

.chat-text {
  font-size: 13px;
  white-space: pre-line;
  color: #20262d;
}

.chat-date {
  font-size: 10px;
  color: #6b7280;
  margin-top: 6px;
  text-align: right;
}

.chat-delivery {
  margin-top: 7px;
  font-size: 11px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 6px;
}

.chat-delivery__checks {
  font-weight: 700;
  letter-spacing: -1px;
  line-height: 1;
}

.chat-delivery__time {
  font-size: 10px;
  color: #6b7280;
}

.chat-delivery--sent {
  color: #4f5f6f;
}

.chat-delivery--delivered {
  color: #4f5f6f;
}

.chat-delivery--read {
  color: #2f74c0;
}

.chat-delivery--failed {
  color: #b13b2f;
}

.chat-delivery--undelivered {
  color: #b13b2f;
}
</style>
